/**
 * BFL FLUX.2 文生图（多版本：klein-04 / klein-09 / max / pro / flex）
 * 官方 API：POST /v1/flux-2-{variant} → polling_url → result.sample
 * 异步轮询封装为同步接口，返回 { urls, provider: 'official', sync: true }
 *
 * 文档：https://docs.bfl.ml/flux_2/flux2_text_to_image
 */
import axios from 'axios'
import { extractApiErrorMessage } from '../../extract-api-error-message'

var BFL_PROXY_PATH = '/bfl'
var POLL_INTERVAL = 3000
var MAX_POLL_ATTEMPTS = 120 // 120 * 3s = 6 minutes max

var VARIANT_ENDPOINTS = {
  'klein-04': '/v1/flux-2-klein-4b',
  'klein-09': '/v1/flux-2-klein-9b-preview',
  'max': '/v1/flux-2-max',
  'pro': '/v1/flux-2-pro',
  'flex': '/v1/flux-2-flex'
}

function getBflConfig() {
  return {
    baseURL: BFL_PROXY_PATH,
    apiKey: process.env.VUE_APP_OFFICIAL_BFL_API_KEY || ''
  }
}

function parsePollingUrl(url) {
  if (!url) return null
  var match = url && url.match(/^https?:\/\/([^/]+\.bfl\.ai)(\/.*)/)
  if (match) {
    return { host: match[1], path: BFL_PROXY_PATH + match[2] }
  }
  return null
}

function getHeaders(apiKey) {
  return {
    'Content-Type': 'application/json',
    'x-key': apiKey
  }
}

/**
 * 递归轮询 BFL 结果接口，直到 status === 'Ready' 或出错/超时
 * @returns {Promise<string>} signed image URL
 */
function pollForResult(apiKey, pollingUrl, bflHost, attempts) {
  return axios.get(pollingUrl, {
    headers: { 'x-key': apiKey, 'X-Bfl-Target': bflHost },
    timeout: 30000
  }).then(function (res) {
    var data = res && res.data
    if (!data) {
      return Promise.reject(new Error('BFL API returned empty response'))
    }
    if (data.status === 'Ready') {
      var url = data.result && data.result.sample
      if (!url) {
        return Promise.reject(new Error('BFL API returned Ready but no sample URL'))
      }
      return url
    }
    if (data.status === 'Request Moderated') {
      return Promise.reject(new Error('Request was moderated by content safety system. Please adjust your prompt and try again.'))
    }
    if (data.status === 'Content Moderated') {
      return Promise.reject(new Error('Generated content was flagged by content moderation. Please modify your prompt and try again.'))
    }
    if (data.status === 'Error') {
      var errMsg = ''
      if (typeof data.error === 'string') {
        errMsg = data.error
      } else if (data.error && typeof data.error === 'object') {
        errMsg = data.error.message || data.error.msg || JSON.stringify(data.error)
      }
      return Promise.reject(new Error(errMsg || 'BFL generation failed'))
    }
    if (attempts <= 0) {
      return Promise.reject(new Error('BFL generation timed out'))
    }
    return new Promise(function (resolve) {
      setTimeout(function () {
        resolve(pollForResult(apiKey, pollingUrl, bflHost, attempts - 1))
      }, POLL_INTERVAL)
    })
  })
}

/**
 * 下载 BFL 签名图片并上传到自有服务器，返回服务器 URL
 * 通过 /bfl 代理下载以避免 Azure Blob Storage 的 CORS 限制
 * @param {string} signedUrl - BFL 返回的临时签名 URL
 * @param {string} filename - 上传文件名
 * @returns {Promise<string>} 服务器图片 URL
 */
function downloadToBlob(signedUrl, filename) {
  var parsed = signedUrl.match(/^https?:\/\/([^/]+)(\/.*)/)
  if (!parsed) {
    return Promise.reject(new Error('Invalid BFL image URL'))
  }
  var host = parsed[1]
  var path = BFL_PROXY_PATH + parsed[2]
  return axios.get(path, {
    responseType: 'arraybuffer',
    timeout: 60000,
    headers: { 'X-Bfl-Target': host }
  }).then(function (res) {
    var blob = new Blob([res.data], { type: 'image/png' })
    console.log('[BFL Flux2] image blob downloaded', blob.size, 'bytes')
    return blob
  })
}

/**
 * 规范化尺寸为 16 的倍数
 */
function normalizeDimension(v, defaultVal) {
  var n = parseInt(v, 10)
  if (!n || n < 16) return defaultVal
  return Math.round(n / 16) * 16
}

/**
 * 提交 BFL FLUX.2 生成请求并轮询结果
 * @param {Object} params
 *   - prompt (string, required)
 *   - variant (string): 'klein-04' | 'klein-09' | 'max' | 'pro' | 'flex'，默认 'pro'
 *   - width (number): 像素宽，16 的倍数，默认 1024
 *   - height (number): 像素高，16 的倍数，默认 1024
 *   - steps (number): 仅 flex，1-50，默认 50
 *   - guidance (number): 仅 flex，1.5-10，默认 4.5
 *   - seed (number): 可选
 *   - safety_tolerance (number): 0-6，默认 2
 *   - prompt_upsampling (boolean): 非 klein，默认 false
 *   - count (number): 生成数量，1-4
 * @returns {Promise<{ urls: string[], provider: 'official', sync: true }>}
 */
export function submitBflFlux2Image(params) {
  var cfg = getBflConfig()
  if (!cfg.apiKey || cfg.apiKey === 'your-bfl-api-key-here') {
    return Promise.reject(new Error('未配置 VUE_APP_OFFICIAL_BFL_API_KEY'))
  }

  var prompt = (params.prompt || '').trim()
  if (!prompt) {
    return Promise.reject(new Error('prompt 必填'))
  }

  var variant = VARIANT_ENDPOINTS[params.variant] ? params.variant : 'pro'
  var endpoint = VARIANT_ENDPOINTS[variant]
  var count = Math.min(4, Math.max(1, parseInt(params.count, 10) || 1))

  var body = {
    prompt: prompt.slice(0, 1000),
    width: normalizeDimension(params.width, 1024),
    height: normalizeDimension(params.height, 1024),
    output_format: 'png'
  }

  // seed
  if (params.seed != null && !isNaN(params.seed)) {
    body.seed = parseInt(params.seed, 10)
  }

  // safety_tolerance
  if (params.safety_tolerance != null) {
    var st = parseInt(params.safety_tolerance, 10)
    if (st >= 0 && st <= 6) body.safety_tolerance = st
  }

  // prompt_upsampling（klein 不支持）
  if (variant !== 'klein-04' && variant !== 'klein-09' && params.prompt_upsampling) {
    body.prompt_upsampling = true
  }

  // flex 专属参数
  if (variant === 'flex') {
    if (params.steps != null) {
      var steps = Math.min(50, Math.max(1, parseInt(params.steps, 10)))
      body.steps = steps
    }
    if (params.guidance != null) {
      var guidance = Math.min(10, Math.max(1.5, parseFloat(params.guidance)))
      body.guidance = guidance
    }
  }

  function submitOne() {
    return axios.post(cfg.baseURL + endpoint, body, {
      headers: getHeaders(cfg.apiKey),
      timeout: 30000
    }).then(function (res) {
      var data = res && res.data
      var rawPollingUrl = data && (data.polling_url || (data.id ? cfg.baseURL + '/v1/get_result?id=' + data.id : null))
      var parsed = parsePollingUrl(rawPollingUrl)
      if (!parsed) {
        return Promise.reject(new Error('BFL API 未返回 polling_url'))
      }
      return pollForResult(cfg.apiKey, parsed.path, parsed.host, MAX_POLL_ATTEMPTS)
        .then(function (signedUrl) {
          return downloadToBlob(signedUrl, 'flux-2-' + variant + '-' + Date.now() + '.png')
        })
    })
  }

  var promises = []
  for (var i = 0; i < count; i++) {
    promises.push(submitOne())
  }

  return Promise.all(promises).then(function (blobs) {
    return { urls: blobs.map(function() { return '' }), blobs: blobs, provider: 'official', sync: true }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}
